import { isDefined } from "@clipboard-health/util-ts";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { useWorker } from "@src/appV2/Worker/api/useWorker";
import { useModalState } from "@src/lib/ui-react";
import { type ReactNode } from "react";

import { useShiftManagementPermission } from "../../../DailyView/hooks/useShiftManagementPermission";
import { useWorkerBlockedStatus } from "../../api/useWorkerBlockedStatus";
import { InviteToShiftDialog } from "./InviteToShiftDialog";

interface UseInviteWorkerToShiftParams {
  workerId: string;
  workplaceId: string;
  /**
   * When `true`, the calling surface explicitly doesn't support invites
   * (e.g. placement channels in chat). Callers map their domain concept
   * to this boolean.
   */
  surfaceDisabled?: boolean;
  /**
   * Forwarded to the dialog's `channelQualification` slot. Chat callers
   * pass the channel metadata's `agentReq`.
   */
  channelQualification?: string;
  /**
   * Caller-supplied blocked status. When provided, the hook skips its
   * internal `useWorkerBlockedStatus` fetch — pass this from list-based
   * surfaces that already carry an exclusion field on each worker to
   * avoid an extra `/exclusion` request per row.
   */
  isBlocked?: boolean;
}

interface UseInviteWorkerToShiftResult {
  canInvite: boolean;
  openInviteDialog: () => void;
  inviteDialogElement: ReactNode;
}

/**
 * Single source of truth for inviting a specific worker to a shift.
 * Consumers wire `openInviteDialog` to a click target when `canInvite` is
 * true and render `inviteDialogElement` somewhere in the tree.
 *
 * Scope: preselected-worker only. A workplace-level "send invite to any
 * worker" entry point would need a separate path — the dialog's
 * empty-state guard short-circuits without qualification context, which
 * only resolves from a known worker (or matching chat channel).
 *
 * `preselectedProfessional.isStarred` is hardcoded to `false` because the
 * only consumer that reads it (`ProfessionalListItem`'s caption switch)
 * also needs `lastShiftDate` / `shiftsWorked` to render — neither of
 * which the hook supplies. Revisit if those fields start flowing through.
 */
export function useInviteWorkerToShift(
  params: UseInviteWorkerToShiftParams
): UseInviteWorkerToShiftResult {
  const {
    workerId,
    workplaceId,
    surfaceDisabled,
    channelQualification,
    isBlocked: isBlockedFromCaller,
  } = params;

  const session = useSession();
  const isWorkerSession = session.type === "AGENT";
  const timezone = session.profile.tmz ?? "";

  // Skips the worker / blocked-status fetches up front for surfaces that
  // can never show the invite, rather than deferring the gate to React Query.
  const isInviteSupportedSurface = !surfaceDisabled && !isWorkerSession;

  const shouldFetchBlockedStatus = !isDefined(isBlockedFromCaller);

  const { canEditShiftTimes } = useShiftManagementPermission();
  const { data: worker } = useWorker(workerId, { enabled: isInviteSupportedSurface });
  const { data: blockedStatus } = useWorkerBlockedStatus({
    workplaceId,
    workerId,
    enabled: isInviteSupportedSurface && shouldFetchBlockedStatus,
  });
  const isBlocked = isDefined(isBlockedFromCaller)
    ? isBlockedFromCaller
    : (blockedStatus?.isBlocked ?? false);

  const modalState = useModalState();

  const canInvite =
    isInviteSupportedSurface && canEditShiftTimes && !isBlocked && isDefined(worker);

  const inviteDialogElement = modalState.modalIsOpen ? (
    <InviteToShiftDialog
      channelQualification={channelQualification}
      matchingFacilityQualifications={worker?.matchingFacilityQualifications}
      modalState={modalState}
      preselectedProfessional={
        isDefined(worker)
          ? {
              id: workerId,
              name: worker.name,
              avatarUrl: worker.profileImageUrl ?? undefined,
              isStarred: false,
            }
          : undefined
      }
      timezone={timezone}
      workerQualification={worker?.qualification}
      workplaceId={workplaceId}
    />
  ) : null;

  return {
    canInvite,
    openInviteDialog: modalState.openModal,
    inviteDialogElement,
  };
}
